import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CollapseBlock, Spinner } from '../../../../common';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import { useGetUserQuery } from '../../../../../redux/services/users';
import AttendanceScheduleTable from './AttendanceScheduleTable.jsx';
import { useSelector } from 'react-redux';
import {
  useSaveAttendanceEventMutation,
  useGetAttendanceScheduleQuery,
} from '../../../../../redux/services/attendance';
import Form from '../../../../antd/form/Form/Form';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect';
import { useGetTeachersQuery } from '../../../../../redux/services/users';
import dayjs from 'dayjs';

const AttendanceSchedulePage = () => {
  const { teacher_id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = useSelector((reduxStore) => reduxStore.auth.user);
  const { errorOccurred } = useToastMessages();
  const { data: teacher_data, isLoading: isLoadingUsers } = useGetUserQuery(
    user.person_id,
  );
  const teacherSchedule = useMemo(() => {
    if (teacher_id !== undefined) return Number(teacher_id);
    if (teacher_data) return teacher_data?.teacher?.uidnumber;
    return undefined;
  }, [teacher_id, teacher_data]);

  const {
    data: attendanceSchedule,
    isLoading: attendanceScheduleLoading,
    isError: attendanceScheduleQueryError,
  } = useGetAttendanceScheduleQuery(teacherSchedule, {
    skip: teacherSchedule === undefined,
  });

  const { data: teachers, isLoadingTeachers } = useGetTeachersQuery();

  const teachersForSelect = useMemo(() => {
    if (!teachers) return [];
    return teachers
      .map((person) => ({
        label: person.full_name,
        value: person.uidnumber,
        last_name: person.person.last_name,
      }))
      .sort((a, b) => a.last_name.localeCompare(b.last_name));
  }, [teachers]);

  const [saveAttendanceEvent] = useSaveAttendanceEventMutation();

  const eventDetail = async (person_id, teacher_id, block) => {
    const values = {
      title: block.type,
      room: block.room,
      subject: block.code,
      teacher_id: person_id,
      created_by: teacher_id,
      started_at: dayjs()
        .hour(block.start_at.split(':')[0])
        .minute(block.start_at.split(':')[1])
        .second(0)
        .format('YYYY-MM-DD HH:mm:ss'),
      ended_at: dayjs()
        .hour(
          Number(block.start_at.split(':')[0]) +
            (block.blocks.split(', ').length - 1),
        )
        .minute(60 - block.blocks.split(', ').length * 10)
        .second(0)
        .format('YYYY-MM-DD HH:mm:ss'),
    };

    const result = await saveAttendanceEvent({ body: values });

    if (Object.hasOwn(result, 'error')) {
      errorOccurred();
      return;
    }

    navigate(`${routes.cmsAttendanceEventDetail}/${result.data.id}`);
  };

  const secondaryActions = [
    {
      onClick: () => navigate(routes.cmsAttendanceCreateCustomEvent),
      title: t('Pridať vlastnú udalosť'),
      permission: 'attendance-events.store',
    },
    {
      onClick: () => navigate(routes.cmsAttendanceCreateScheduleEvent),
      title: t('Pridať udalosť z rozvrhu'),
      permission: 'attendance-events.store',
    },
  ];

  const initials = { created_by: teacherSchedule };

  const scheduleData = useMemo(() => {
    if (!attendanceSchedule || attendanceScheduleQueryError) return undefined;
    if ('message' in attendanceSchedule) return undefined;
    return Object.values(attendanceSchedule);
  }, [attendanceSchedule, attendanceScheduleQueryError]);

  if (isLoadingUsers || attendanceScheduleLoading || isLoadingTeachers) {
    return <Spinner />;
  }
  return (
    <>
      <AppBarCMS
        title={t('Rozvrh')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        permissionsBasic={'fast-links'}
        secondaryActions={secondaryActions}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock title={t('Rozvrh')}>
          <Form initialValues={initials}>
            <div style={{ maxWidth: '300px' }}>
              <FormSelect
                label={t('Učiteľ')}
                name="created_by"
                options={teachersForSelect}
                placeholder={t('Vyhľadať učiteľa')}
                allowClear
                onChange={(value) => {
                  navigate(
                    value
                      ? `/cms/attendance-schedule/${value}`
                      : '/cms/attendance-schedule',
                  );
                }}
              />
            </div>
          </Form>
          {scheduleData === undefined ? (
            <div>
              {attendanceSchedule?.message
                ? t(attendanceSchedule.message)
                : t('Rozvrh nie je dostupný.')}
            </div>
          ) : (
            <AttendanceScheduleTable
              onClick={eventDetail}
              person={teacher_id === undefined ? null : user.person_id}
              teacher={teacherSchedule}
              blocks={scheduleData}
            />
          )}
        </CollapseBlock>
      </div>
    </>
  );
};

export default AttendanceSchedulePage;
